/**
 * web/screens/characterSelect.ts
 * Character-select screen with looping IDLE-frame previews.
 *
 * Each card renders a 120×120 viewport whose background image is the
 * full sprite sheet. A `setInterval` shifts `background-position-x` at
 * the IDLE clip's fps so the character appears to breathe. Selecting
 * a card persists `activeCharacterId` to SaveData and navigates to
 * `/world-map`.
 *
 * @module web/screens/characterSelect
 */

/// <reference lib="dom" />

import type { Route } from '../router';
import { injectStyles } from '../styles';
import * as SaveManager from '@src/game/services/saveManager';
import { CHARACTERS } from '@src/game/data/characterData';
import { CHARACTER_IDS, type CharacterId } from '@src/game/types/character';
import { setActiveCharacter } from '@src/game/data/activeCharacter';
import Logger from '@src/game/utils/logger';

import novaUrl from '../../src/assets/sprites/nova-v2-sheet.png';
import avonUrl from '../../src/assets/sprites/avon-sheet.png';
import belleUrl from '../../src/assets/sprites/belle-sheet.png';

const SHEET_URLS: Record<CharacterId, string> = {
  nova: novaUrl,
  avon: avonUrl,
  belle: belleUrl,
};

const PREVIEW_SIZE = 120;

/**
 * On-card height of every character's visible art, in px. Uniform across
 * characters so the three cards read as a single consistent lineup.
 */
const PREVIEW_CONTENT_HEIGHT = 84;

/** Y coordinate inside the card where every character's feet rest. */
const PREVIEW_BASELINE_Y = 102;

/** Build a single character card with an animated IDLE preview. */
function buildCard(
  id: CharacterId,
  selectedRef: { current: CharacterId },
  onSelect: (id: CharacterId) => void,
  timers: number[],
): HTMLDivElement {
  const meta = CHARACTERS[id];
  const idle = meta.clips.IDLE;
  const card = document.createElement('div');
  card.className = 'sf-card';
  card.dataset['characterId'] = id;
  card.style.borderColor = id === selectedRef.current ? meta.accentColor : 'transparent';

  const preview = document.createElement('div');
  preview.className = 'sf-card-preview';
  preview.style.width = `${PREVIEW_SIZE}px`;
  preview.style.height = `${PREVIEW_SIZE}px`;
  preview.style.backgroundImage = `url(${SHEET_URLS[id]})`;
  preview.style.backgroundRepeat = 'no-repeat';
  // DECISION: Scale by measured content height, not frameWidth. All three
  // sheets share a 60px frame but the art inside differs (Nova 29px tall vs
  // Avon/Belle 44px), so frame-based scaling drew Avon and Belle ~57% taller
  // than Nova. In play the runtime normaliser equalises them; a CSS
  // background can't run that, so reproduce its result from previewMetrics.
  const metrics = meta.previewMetrics;
  const scale = PREVIEW_CONTENT_HEIGHT / metrics.contentHeight;
  const frameW = meta.sheet.frameWidth;
  preview.style.backgroundSize =
    `${meta.sheet.sheetWidth * scale}px ${meta.sheet.sheetHeight * scale}px`;
  // Align every character's feet to one baseline so the cards line up.
  preview.style.backgroundPositionY = `${PREVIEW_BASELINE_Y - metrics.bottom * scale}px`;

  const frameCount = Math.max(1, idle.end - idle.start + 1);
  let frameIndex = 0;
  const draw = (): void => {
    const abs = idle.start + frameIndex;
    // Centre the frame's content, not the frame — art is off-centre in-frame.
    const contentX = (abs * frameW + metrics.centerX) * scale;
    preview.style.backgroundPositionX = `${PREVIEW_SIZE / 2 - contentX}px`;
  };
  draw();
  const fps = idle.fps || 3;
  const timer = window.setInterval(() => {
    frameIndex = (frameIndex + 1) % frameCount;
    draw();
  }, 1000 / fps);
  timers.push(timer);

  const name = document.createElement('h3');
  name.className = 'sf-card-title';
  name.textContent = meta.name;
  name.style.color = meta.accentColor;
  const desc = document.createElement('p');
  desc.className = 'sf-card-subtitle';
  desc.textContent = meta.description;

  card.append(preview, name, desc);
  card.addEventListener('click', () => onSelect(id));
  return card;
}

/** Character-select route factory. */
export const characterSelectScreen: Route = (router) => {
  injectStyles();
  const screen = document.createElement('section');
  screen.className = 'sf-screen';
  screen.dataset['screen'] = 'character-select';

  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'sf-btn sf-btn-back';
  back.textContent = '← Back';
  back.addEventListener('click', () => router.back('/'));

  const heading = document.createElement('h1');
  heading.className = 'sf-screen-title';
  heading.textContent = 'CHOOSE YOUR COURIER';

  const grid = document.createElement('div');
  grid.className = 'sf-card-grid';

  const selectedRef: { current: CharacterId } = { current: 'nova' };
  const timers: number[] = [];
  const cards: HTMLDivElement[] = [];

  const onSelect = (id: CharacterId): void => {
    selectedRef.current = id;
    for (const c of cards) {
      const cid = c.dataset['characterId'] as CharacterId;
      c.style.borderColor = cid === id ? CHARACTERS[cid].accentColor : 'transparent';
    }
  };

  for (const id of CHARACTER_IDS) {
    const card = buildCard(id, selectedRef, onSelect, timers);
    cards.push(card);
    grid.append(card);
  }

  let destroyed = false;
  void SaveManager.load().then((data) => {
    if (destroyed) return;
    if (data?.selectedCharacter) onSelect(data.selectedCharacter);
  });

  const confirm = document.createElement('button');
  confirm.type = 'button';
  confirm.className = 'sf-btn sf-btn-primary';
  confirm.style.marginTop = '1rem';
  confirm.textContent = 'Confirm';
  confirm.addEventListener('click', () => {
    // DECISION: Persist the selection *before* navigating so the world-map
    // screen sees the correct selectedCharacter on its first read.
    // Wrap in try/catch — SaveManager.save() can throw if localStorage is
    // unavailable or over quota. We must not navigate on failure (the new
    // screen would read a stale character) and must not let it surface as
    // an unhandled rejection.
    confirm.disabled = true;
    void (async (): Promise<void> => {
      try {
        const existing = await SaveManager.load();
        const next = existing ?? SaveManager.createDefaultSave();
        next.selectedCharacter = selectedRef.current;
        await SaveManager.save(next);
        setActiveCharacter(selectedRef.current);
        router.navigate('/world-map');
      } catch (err) {
        Logger.warn(
          'CharacterSelect',
          `Failed to persist selection — staying on screen: ${String(err)}`,
        );
        if (!destroyed) confirm.disabled = false;
      }
    })();
  });

  screen.append(back, heading, grid, confirm);

  const root = document.getElementById('app');
  root?.append(screen);

  return {
    destroy: () => {
      destroyed = true;
      for (const t of timers) window.clearInterval(t);
      screen.remove();
    },
  };
};
